import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import ProductCard from '@/components/ProductCard';
import PageHead from '@/components/PageHead';
import { fetchProducts, CATEGORIES } from '@/lib/api';

export default function BoutiquePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || 'all';
  const [category, setCategory] = useState(initialCategory);
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProducts({ category })
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q));
  }, [products, search]);

  const onCategory = (slug) => {
    setCategory(slug);
    if (slug === 'all') searchParams.delete('category');
    else searchParams.set('category', slug);
    setSearchParams(searchParams);
  };

  return (
    <div data-testid="boutique-page">
      <PageHead title="Boutique B2B" description="Catalogue ALOFI SA — Alimentation, Maison, Multimédia, Vins & Spiritueux. 39 produits sélectionnés. Demandez votre devis." />
      {/* Header */}
      <section className="hero-bg relative noise-overlay">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px] bg-white/80">Boutique B2B</Badge>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-semibold tracking-tight">Catalogue ALOFI SA</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">Une sélection rigoureuse pour vos rayons. Aucun prix affiché — demandez votre devis personnalisé.</p>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur border-b border-border" data-testid="boutique-filters">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit, une marque..."
              className="pl-9 rounded-xl"
              data-testid="boutique-search-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-muted-foreground"><SlidersHorizontal className="w-3.5 h-3.5" />Catégories :</span>
            <button
              onClick={() => onCategory('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${category === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-white border-border hover:border-primary'}`}
              data-testid="boutique-filter-all"
            >
              Tous
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => onCategory(c.slug)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${category === c.slug ? 'bg-primary text-primary-foreground border-primary' : 'bg-white border-border hover:border-primary'}`}
                data-testid={`boutique-filter-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
          <p className="text-sm text-muted-foreground" data-testid="boutique-result-count">
            {loading ? '…' : `${filtered.length} produit${filtered.length > 1 ? 's' : ''}`}
          </p>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="rounded-2xl border-border p-3">
                <Skeleton className="w-full aspect-[4/3] rounded-xl" />
                <Skeleton className="h-4 w-2/3 mt-3" />
                <Skeleton className="h-4 w-full mt-2" />
              </Card>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center rounded-2xl">
            <h3 className="font-heading text-lg font-semibold">Aucun résultat</h3>
            <p className="text-sm text-muted-foreground mt-1">Essayez d’autres mots-clés ou changez de catégorie.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="boutique-products-grid">
            {filtered.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
